// ============================================
// pages/release-orders/ROPaymentsLedger.tsx
// Read-only buyer receivables ledger rolled up from release_orders.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { Wallet, Loader2, XCircle, Download } from 'lucide-react';
import { seasonsAPI, releaseOrdersAPI } from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

interface Row {
  buyer_name: string;
  buyer_type: string;
  open_ros: number;
  closed_ros: number;
  total_ros: number;
  total_quantity_mts: number;
  total_value: number;
  payment_received: number;
  balance_receivable: number;
  receipt_pct: number;
}

export const ROPaymentsLedger: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buyerTypeFilter, setBuyerTypeFilter] = useState<string>('all');

  useEffect(() => {
    (async () => {
      try {
        const s = await seasonsAPI.list();
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await releaseOrdersAPI.list(selectedSeasonId);
        setOrders(Array.isArray(res.data) ? res.data : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load ROs');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId]);

  const filtered = useMemo(() => {
    if (buyerTypeFilter === 'all') return orders;
    return orders.filter((o) => (o.buyer_type || '') === buyerTypeFilter);
  }, [orders, buyerTypeFilter]);

  const rows = useMemo((): Row[] => {
    const map: Record<string, Row> = {};
    for (const o of filtered) {
      const key = `${o.buyer_name}|${o.buyer_type}`;
      if (!map[key]) {
        map[key] = {
          buyer_name: o.buyer_name,
          buyer_type: o.buyer_type || 'other',
          open_ros: 0, closed_ros: 0, total_ros: 0,
          total_quantity_mts: 0, total_value: 0,
          payment_received: 0, balance_receivable: 0, receipt_pct: 0,
        };
      }
      const r = map[key];
      r.total_ros++;
      if (o.status === 'closed') r.closed_ros++;
      else r.open_ros++;
      r.total_quantity_mts += num(o.quantity_mts);
      r.total_value += num(o.total_value);
      r.payment_received += num(o.payment_received);
      r.balance_receivable += num(o.balance_receivable);
    }
    return Object.values(map)
      .map((r) => {
        r.receipt_pct = r.total_value > 0 ? (r.payment_received / r.total_value) * 100 : 0;
        return r;
      })
      .sort((a, b) => b.balance_receivable - a.balance_receivable);
  }, [filtered]);

  const totals = useMemo(() => ({
    ros: rows.reduce((s, r) => s + r.total_ros, 0),
    value: rows.reduce((s, r) => s + r.total_value, 0),
    received: rows.reduce((s, r) => s + r.payment_received, 0),
    balance: rows.reduce((s, r) => s + r.balance_receivable, 0),
  }), [rows]);

  const scoreColor = (v: number) => v >= 95 ? 'text-green-700' : v >= 70 ? 'text-yellow-700' : 'text-red-700';
  const typeLabel = (t: string) => ({ fci: 'FCI', civil_supplies: 'Civil Supplies', private: 'Private', other: 'Other' } as any)[t] || t;
  const handlePrint = () => window.print();

  if (loading && rows.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-purple-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto print:max-w-none">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Wallet className="w-7 h-7 text-purple-600" />
            RO Payments Ledger
          </h1>
          <p className="text-sm text-gray-500 mt-1">Buyer receivables — rolled up across all release orders</p>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white font-semibold rounded-lg text-sm">
          <Download className="w-4 h-4" />Print
        </button>
      </div>

      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 print:hidden">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
          <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
            {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Buyer Type</label>
          <select value={buyerTypeFilter} onChange={(e) => setBuyerTypeFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All</option>
            <option value="fci">FCI</option>
            <option value="civil_supplies">Civil Supplies</option>
            <option value="private">Private</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2"><XCircle className="w-4 h-4" />{error}</div>}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Total ROs</p>
          <p className="text-2xl font-bold text-gray-900">{totals.ros}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Total Value</p>
          <p className="text-2xl font-bold text-gray-900">{formatAmount(totals.value)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Received</p>
          <p className="text-2xl font-bold text-green-700">{formatAmount(totals.received)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Outstanding</p>
          <p className="text-2xl font-bold text-orange-600">{formatAmount(totals.balance)}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Buyer</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Open</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Closed</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Total ROs</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Qty (MT)</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Total Value</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Received</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Outstanding</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Collected %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400">No release orders in this season.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-900">{r.buyer_name}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">
                      {typeLabel(r.buyer_type)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-orange-600">{r.open_ros}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{r.closed_ros}</td>
                  <td className="px-3 py-2 text-right">{r.total_ros}</td>
                  <td className="px-3 py-2 text-right">{num(r.total_quantity_mts).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-medium">{formatAmount(r.total_value)}</td>
                  <td className="px-3 py-2 text-right text-green-700">{formatAmount(r.payment_received)}</td>
                  <td className="px-3 py-2 text-right text-orange-600 font-bold">{formatAmount(r.balance_receivable)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${scoreColor(r.receipt_pct)}`}>{r.receipt_pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr className="font-bold">
                  <td colSpan={4} className="px-3 py-2">TOTAL</td>
                  <td className="px-3 py-2 text-right">{totals.ros}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right">{formatAmount(totals.value)}</td>
                  <td className="px-3 py-2 text-right text-green-700">{formatAmount(totals.received)}</td>
                  <td className="px-3 py-2 text-right text-orange-600">{formatAmount(totals.balance)}</td>
                  <td className="px-3 py-2 text-right">
                    {totals.value > 0 ? ((totals.received / totals.value) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ROPaymentsLedger;
